// PinnacleBasicTable.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { NO_DATA_TO_DISPLAY_URL } from '../../utils/constants';

type ColumnConfig<T> = {
    key: keyof T | string;
    label: string;
    render?: (row: T) => React.ReactNode;
    sx?: object;
    skipTooltip?: boolean;
};

type ReusableTableProps<T extends Record<string, any>> = {
    columns: ColumnConfig<T>[];
    rows: T[];
    headerColor: string;
    rowIdKey?: keyof T;
    hasMultiSelect?: boolean;
    selectedIds?: string[];
    disabledActions?: boolean;
    onSelectionChange?: (ids: string[]) => void;
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
    onPreboardExamineeExam?: (row: T) => void;
};

export function PinnacleBasicTable<T extends Record<string, any>>({
    columns,
    rows,
    headerColor,
    rowIdKey = '_id' as keyof T,
    hasMultiSelect,
    selectedIds,
    disabledActions,
    onSelectionChange,
    onEdit,
    onDelete,
    onPreboardExamineeExam
}: ReusableTableProps<T>) {
    // internal Set for fast contains/add/remove
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set());
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    // Utility to get row id as string
    const getId = (row: T, index: number) =>
        String((row[rowIdKey] ?? index) as string);

    // sync controlled selectedIds -> localSelected
    useEffect(() => {
        if (Array.isArray(selectedIds)) {
            setLocalSelected(new Set(selectedIds));
        }
    }, [selectedIds]);

    // When rows change, drop any selected ids not present in rows (keeps selection consistent)
    useEffect(() => {
        const idsInRows = new Set(rows.map((r, i) => getId(r, i)));
        setLocalSelected((prev) => {
            const filtered = new Set(Array.from(prev).filter((id) => idsInRows.has(id)));
            // notify parent when we filter (keeps parent in sync)
            onSelectionChange?.(Array.from(filtered));
            return filtered;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    // expose boolean states for header
    const selectedCount = localSelected.size;

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
    const handleMenuClose = () => setMenuAnchor(null);

    const selectAll = () => {
        const allIds = rows.map((r, i) => getId(r, i));
        setLocalSelected(new Set(allIds));
        onSelectionChange?.(allIds);
        handleMenuClose();
    };
    const selectNone = () => {
        setLocalSelected(new Set());
        onSelectionChange?.([]);
        handleMenuClose();
    };

    const toggleRow = (row: T, index: number) => {
        const id = getId(row, index);
        setLocalSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            onSelectionChange?.(Array.from(next));
            return next;
        });
    };

    return (
        <TableContainer
            component={Paper}
            sx={{
                width: '100%',
                overflowX: 'auto'
            }}
        >
            <Table
                sx={{
                    tableLayout: 'auto',
                    width: '100%',
                    minWidth: 650
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: headerColor }}>
                        {hasMultiSelect && (
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    py: 1.5,
                                    width: '1%',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Button
                                        variant="text"
                                        onClick={handleMenuOpen}
                                        sx={{
                                            color: 'white',
                                            textTransform: 'none',
                                            minWidth: 0,
                                            p: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedCount === rows.length && rows.length > 0}
                                            indeterminate={
                                                selectedCount > 0 &&
                                                selectedCount < rows.length
                                            }
                                            sx={{
                                                color: 'white',
                                                p: 0,
                                                '&.Mui-checked': { color: 'white' },
                                            }}
                                        />
                                        <ArrowDropDownIcon sx={{ color: 'white' }} />
                                    </Button>
                                </Box>

                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem onClick={selectAll}>All Users</MenuItem>
                                    <MenuItem onClick={selectNone}>None</MenuItem>
                                </Menu>
                            </TableCell>
                        )}

                        {columns.map((col) => (
                            <TableCell key={String(col.key)} sx={{ color: 'white', fontWeight: 600, py: 1.5 }}>
                                {col.label.toUpperCase()}
                            </TableCell>
                        ))}

                        {(onEdit || onDelete) && (
                            <TableCell sx={{ color: 'white', fontWeight: 600, py: 1.5 }}>ACTION</TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length + (hasMultiSelect ? 1 : 0) + (onEdit || onDelete ? 1 : 0)} sx={{ p: 0 }}>
                                <Box sx={{ height: '170px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#818181' }}>
                                    <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '260px', maxWidth: '100%', objectFit: 'contain', mb: 1 }} />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, index) => {
                            const id = getId(row, index);
                            const checked = localSelected.has(id);

                            return (
                                <TableRow key={id}>
                                    {hasMultiSelect && (
                                        <TableCell>
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => toggleRow(row, index)}
                                                sx={{ p: 0 }}
                                            />
                                        </TableCell>
                                    )}

                                    {columns.map((col) => {
                                        const value = col.render ? col.render(row) : (row[col.key as keyof T] as any);
                                        return (
                                            <TableCell key={String(col.key)} sx={col.sx}>
                                                <Tooltip title={col.skipTooltip ? '' : String(row[col.key as keyof T] ?? '')} arrow>
                                                    <span style={{ display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                                        {value}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        );
                                    })}

                                    {(onEdit || onDelete || onPreboardExamineeExam) && (
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                                                }}
                                            >
                                                {onEdit && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<ModeEditOutlineOutlinedIcon />}
                                                        size="small"
                                                        sx={{ backgroundColor: '#003b71' }}
                                                        onClick={() => onEdit(row)}
                                                        disabled={disabledActions}
                                                    >
                                                        edit
                                                    </Button>
                                                )}
                                                {onDelete && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<DeleteOutlineOutlinedIcon />}
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => onDelete(row)}
                                                        disabled={disabledActions}
                                                    >
                                                        delete
                                                    </Button>
                                                )}

                                                {onPreboardExamineeExam && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<NoteAltOutlinedIcon />}
                                                        size="small"
                                                        sx={{ borderColor: '1px solid #003b71' }}
                                                        onClick={() => onPreboardExamineeExam(row)}
                                                        disabled={disabledActions}
                                                    >
                                                        exams
                                                    </Button>
                                                )}
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
